import React, { useCallback, useEffect, useState } from 'react';
import { Grid, Box, Typography } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useController } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from "react-toastify";
import { createMessage, updateMessage, deleteMessage, getCampaignEchos } from '../../../../../redux/thunks/bannerAdCampaignThunk';
import { selectDurationOptions } from '../../../../../redux/reducers/durationReducer';
import { CustomButton, CustomReactSelect } from '../../../../shared';
import { Upload } from '../../../../../assets';

const initialValue = {
    echoName: '',
    echoDuration: '',
    echoType: '0',
    echoBannerAd: null,
    echoBannerAdUrl: null,
};

const updateBannerAdSchema = yup
    .object({
        echoName: yup.string().required('Echo Name is required'),
        echoDuration: yup.string().required('Please Select Echo Duration'),
        echoType: yup.string().required('Please Select Echo Type'),
        echoBannerAd: yup.mixed().notRequired(),
        echoBannerAdUrl: yup.mixed().notRequired(),
    })
    .required();

const createBannerAdSchema = yup
    .object({
        echoName: yup.string().required('Echo Name is required'),
        echoDuration: yup.string().required('Please Select Echo Duration'),
        echoType: yup.string().required('Please Select Echo Type'),
        echoBannerAd: yup.mixed().required('Please Upload an Image'),
        echoBannerAdUrl: yup.mixed(),
    })
    .required();

const getValidationSchema = (id) => {
    return id ? updateBannerAdSchema : createBannerAdSchema;
};

const ECHO_TYPE = [
    { label: 'Non Engaged', value: "0" },
    { label: 'Engaged', value: "1" },
    { label: 'Default', value: "2" },
];

function BannerAdEchoBuilder(props) {
    const { id, name, imageUrl, duration, type, handleAfterEchoSave, handleEchoRowState } = props;
    const dispatch = useDispatch();
    const echoDurations = useSelector(selectDurationOptions());
    const { company } = useSelector((state) => state.company);
    const { campaign } = useSelector((state) => state.campaign);

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors },
        control,
    } = useForm({
        resolver: yupResolver(getValidationSchema(id)),
        defaultValues: initialValue,
    });

    const {
        field: {
            value: echoDuration,
            onChange: onEchoDurationChange,
            ...restEchoDurationProps
        },
    } = useController({ name: 'echoDuration', control });

    const {
        field: {
            value: echoType,
            onChange: onEchoTypeChange,
            ...restEchoTypeProps
        },
    } = useController({ name: 'echoType', control });

    const onDrop = useCallback(
        (acceptedFiles) => {
            const file = acceptedFiles[0];
            const blobUrl = URL.createObjectURL(file);
            setValue('echoBannerAdUrl', blobUrl);
            setValue('echoBannerAd', file);
        },
        [setValue]
    );

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            
        },
    });

    const onFileSelect = (file) => {
        setValue('echoBannerAdUrl', URL.createObjectURL(file));
        setValue('echoBannerAd', file);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        onFileSelect(file);
    };

    const handleDurationChange = (selectedOption) => {
        onEchoDurationChange(
            selectedOption ? selectedOption.value : selectedOption
        );
    };

    const handleTypeChange = (selectedOption) => {
        onEchoTypeChange(
            selectedOption ? selectedOption.value : selectedOption
        );
    };

    const resetEchoBuilderFields = () => {
        setValue('echoName', "");
        setValue('echoDuration', "");
        setValue('echoType', "");
        setValue('echoBannerAd', null);
        setValue('echoBannerAdUrl', null);
    };

    const onSubmit = (data) => {
        const formData = new FormData();
        formData.append('name', data.echoName);
        formData.append('duration', data.echoDuration);
        formData.append('engagement_enabled', data.echoType);

        if (data.echoBannerAd) {
            formData.append('image', data.echoBannerAd);
        }

        if(id) {
            dispatch(updateMessage({
                companyId: company.id,
                campaignId: campaign.id,
                messageId: id,
                data: formData,
            })).then((result) => {
                handleEchoRowState();
                resetEchoBuilderFields();
                dispatch(getCampaignEchos({
                    companyId: company.id,
                    campaignId: campaign.id,
                }));
            }).catch((error) => {
                toast.error(error);
            });
        } else {
            dispatch(createMessage({
                companyId: company.id,
                campaignId: campaign.id,
                messageId: id,
                data: formData,
            })).then((result) => {
                resetEchoBuilderFields();
                handleAfterEchoSave();
                dispatch(getCampaignEchos({
                    companyId: company.id,
                    campaignId: campaign.id,
                }));
            }).catch((error) => {
                toast.error(error);
            });
        }
        
    };

    const handleSave = () => {
        handleSubmit(onSubmit);
    };

    const handleDelete = (id) => {
        dispatch(deleteMessage({
            companyId: company.id,
            campaignId: campaign.id,
            messageId: id,
        })).then((result) => {
            resetEchoBuilderFields();
            dispatch(getCampaignEchos({
                companyId: company.id,
                campaignId: campaign.id,
            }));
        }).catch((error) => {
            toast.error(error);
        });
    };

    useEffect(()=> {
        if(id !== null){
            setValue('echoName', name);
            setValue('echoDuration', duration);
            setValue('echoType', type);
            setValue('echoBannerAdUrl', imageUrl);
        }

        setValue('resolver', yupResolver(getValidationSchema(id)));
    }, [id]);
    
    return (
        <Grid container>
            <form
                id="bannerAdEchoRow"
                onSubmit={handleSubmit(onSubmit)}
                className="w-100"
            >
                <Grid item xs={12}>
                    {/* Banner Ad Echo */}
                    <Box className="padding-12px">
                        <Grid container spacing="12px">
                            {/* Banner Ad Echo Name */}
                            <Grid item xs={4}>
                                <Box className="flex-column gap-6 w-100 ">
                                    <input
                                        type="text"
                                        placeholder="Banner Ad Echo Name"
                                        className="custom-text-field padding-tb-6-rl-10"
                                        {...register('echoName')}
                                    />
                                    <span className="text-xs-regular error-500">
                                        {errors.echoName?.message}
                                    </span>
                                </Box>
                            </Grid>
                            {/* Banner Ad Echo Duration */}
                            <Grid item xs={4}>
                                <CustomReactSelect
                                    options={echoDurations}
                                    placeholder="Select Echo Duration"
                                    handleFilter={(option) =>
                                        handleDurationChange(option)
                                    }
                                    selectedOption={
                                        echoDuration
                                            ? echoDurations.find(
                                                  (x) =>
                                                      x.value === echoDuration
                                              )
                                            : echoDuration
                                    }
                                    {...restEchoDurationProps}
                                />
                                <span className="text-xs-regular error-500">
                                    {errors.echoDuration?.message}
                                </span>
                            </Grid>
                            {/* Banner Ad Echo Type */}
                            <Grid item xs={4}>
                                <CustomReactSelect
                                    options={ECHO_TYPE}
                                    placeholder="Select Echo Type"
                                    handleFilter={(option) =>
                                        handleTypeChange(option)
                                    }
                                    selectedOption={
                                        echoType
                                            ? ECHO_TYPE.find(
                                                  (x) => x.value === echoType
                                              )
                                            : echoType
                                    }
                                    {...restEchoTypeProps}
                                />
                                <span className="text-xs-regular error-500">
                                    {errors.echoType?.message}
                                </span>
                            </Grid>
                            <Grid item xs={12}>
                                {/* Banner Ad Echo Image */}
                                <Box className="pb-12px text-align-center">
                                    {getValues('echoBannerAdUrl') && (
                                        <img
                                            src={getValues('echoBannerAdUrl')}
                                            width="50%"
                                            height="auto"
                                        />
                                    )}
                                </Box>
                                {/* Banner Ad Upload Echo Image */}
                                <Box className="pb-12px">
                                    <Box
                                        className="flex-column-center border-1-gray-300 br-16px padding-tb-16-rl-24 wh-100"
                                        {...getRootProps()}
                                    >
                                        <Box className="wh-40px bg-gray-100 br-50 border-6-gray-50">
                                            <Upload />
                                        </Box>
                                        <Box className="flex-row gap-4">
                                            <Typography className="text-sm-medium primary-600">
                                                Click to upload
                                            </Typography>
                                            <Typography className="text-sm-regular gray-500">
                                                or drag and drop
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography className="text-sm-regular gray-500">
                                                PNG, JPG or JPEG
                                            </Typography>
                                        </Box>
                                        <input
                                            {...getInputProps()}
                                            id="bannerAdFileInput"
                                        />

                                        <input
                                            type="file"
                                            onChange={handleFileChange}
                                            style={{ display: 'none' }}
                                            id="fileInput"
                                        />

                                        <span className="text-xs-regular error-500">
                                            {errors.echoBannerAd?.message}
                                        </span>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box className="padding-12px pt-0 flex-row gap-4">
                        <CustomButton
                            title="Save"
                            titleColor="#FFFFFF"
                            buttonColor="#AD509E"
                            handleClick={handleSave}
                            type="submit"
                        />

                        {id && <CustomButton
                            title="Delete"
                            titleColor="#FFFFFF"
                            buttonColor="#FDA29B"
                            handleClick={() => handleDelete(id)}
                        />}
                    </Box>
                </Grid>
            </form>
        </Grid>
    );
};

BannerAdEchoBuilder.defaultProps = {
    id:null,
    name:"",
    imageUrl: null,
    duration:"", 
    type:"",
    handleAfterEchoSave: () => {},
    handleEchoRowState: () => {},
};

export default BannerAdEchoBuilder;
